import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable, combineLatest, filter, map, of, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { merge } from 'lodash';
import { BazaFormValidatorService, BazaLoadingService } from '@scaliolabs/baza-core-ng';
import { BazaNcIntegrationListingsDto } from '@scaliolabs/baza-nc-integration-shared';
import {
    BazaNcBootstrapDto,
    BazaNcPurchaseFlowLimitsForPurchaseResponse,
    BazaNcPurchaseFlowTransactionType,
    BazaNcRecurringTransactionRequest,
    PurchaseFlowDto,
} from '@scaliolabs/baza-nc-shared';
import { Calculator } from '@scaliolabs/baza-web-ui-components';
import { BazaLinkUtilSharedService, BazaWebUtilSharedService, LoaderService, StorageService } from '@scaliolabs/baza-web-utils';

import {
    GetLimitsForPurchase,
    PatchStartPurchase,
    PaymentConsentForm,
    PaymentMethodsConfig,
    PurchaseState,
    StartRecurringPurchase,
    SubmitPurchase,
} from '../data-access';

@UntilDestroy()
@Component({
    selector: 'app-purchase-payment',
    templateUrl: './payment.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PurchasePaymentComponent implements OnInit, OnDestroy {
    @Input()
    initData?: BazaNcBootstrapDto;

    @Input()
    config?: PaymentMethodsConfig;

    cart$ = this.store.select(PurchaseState.cart);
    limitsForPurchase$ = this.store.select(PurchaseState.limitsForPurchase);
    numberOfShares$ = this.store.select(PurchaseState.numberOfShares);
    purchaseStart$ = this.store.select(PurchaseState.purchaseStart);
    recurring$ = this.store.select(PurchaseState.recurring);

    isPurchaseAboveLimit = false;
    isSubmitBtnDisabled = true;

    frequency: string;
    i18nBasePath = 'dwpf.payment';
    paymentMethodAdded = false;
    pfConfig = this.store.selectSnapshot(PurchaseState.pfConfig);
    paymentConsentForm: PaymentConsentForm;

    dataStream$: Observable<{
        purchaseStart?: PurchaseFlowDto;
        number?: number;
        entity?: BazaNcIntegrationListingsDto;
        recurring?: boolean;
    }>;

    constructor(
        private readonly fb: UntypedFormBuilder,
        private readonly loaderService: LoaderService,
        private readonly router: Router,
        private readonly storageService: StorageService,
        private readonly store: Store,
        public readonly formValidatorService: BazaFormValidatorService,
        public readonly loader: BazaLoadingService,
        public readonly uts: BazaLinkUtilSharedService,
        public readonly wts: BazaWebUtilSharedService,
    ) {
        const calc: Calculator = this.storageService.getObject('calc');

        this.frequency = calc?.frequency;

        this.onFormReset();
    }

    ngOnInit(): void {
        this.checkConfig();
        this.initializeDataStream();
    }

    initializeDataStream() {
        this.loaderService.show();

        this.dataStream$ = combineLatest([this.purchaseStart$, this.numberOfShares$, this.cart$, this.recurring$]).pipe(
            map(([purchaseStart, number, entity, recurring]) => ({
                purchaseStart,
                number,
                entity,
                recurring,
            })),
            filter((pair) => !!pair.entity && !!pair.number && !!pair.purchaseStart),
            take(1),
            switchMap((pair) => {
                const total = (pair.number || 1) * pair.entity.pricePerShareCents;

                return this.store.dispatch(new GetLimitsForPurchase(total, pair.entity.offeringId)).pipe(
                    filter((res) => !!res),
                    withLatestFrom(this.limitsForPurchase$),
                    take(1),
                    switchMap(([_, limitsForPurchase]) => {
                        if (this.config?.showCreditCard) {
                            this.validateLimitsForPurchase(pair.purchaseStart, limitsForPurchase);
                        }
                        return of(pair);
                    }),
                );
            }),
        );
    }

    ngOnDestroy(): void {
        this.loaderService.hide();
    }

    // onFormReset
    public onFormReset(): void {
        this.paymentConsentForm = this.fb.group({
            authorizationProvided: [false, Validators.requiredTrue],
        }) as PaymentConsentForm;
    }

    acceptConsent(event: Event) {
        if (!(event?.target as HTMLElement)?.classList?.toString()?.toLocaleLowerCase()?.includes('linkify')) {
            const consentCheckboxCtrl = this.paymentConsentForm.get('authorizationProvided');

            consentCheckboxCtrl.setValue(!consentCheckboxCtrl.value);
            consentCheckboxCtrl.markAsDirty();
            consentCheckboxCtrl.updateValueAndValidity();
        }
    }

    checkConfig() {
        const defaultConfig: PaymentMethodsConfig = {
            showAccountBalance: this.pfConfig?.dwollaSupport,
            showBankAccount: true,
            showCreditCard: true,
        };

        this.config = merge(defaultConfig, this.config);
    }

    // onFormSubmit
    public onFormSubmit(paymentFormEl: HTMLFormElement, purchaseStart: PurchaseFlowDto, offeringId: string, recurring: boolean): void {
        if (this.formValidatorService.isFormValid(this.paymentConsentForm, paymentFormEl)) {
            const requests = [];

            requests.push(new SubmitPurchase(purchaseStart.id));

            if (recurring) {
                const calc: Calculator = this.storageService.getObject('calc');

                const recurringRequest: BazaNcRecurringTransactionRequest = {
                    offeringId: offeringId,
                    numberOfShares: purchaseStart.numberOfShares,
                    transactionType: purchaseStart.transactionType,
                    investmentFrequency: calc.frequency,
                };

                requests.push(new StartRecurringPurchase(recurringRequest));
            }

            this.store
                .dispatch(requests)
                .pipe(
                    untilDestroyed(this),
                    tap(() => {
                        this.wts.refreshInitData$.next();
                        this.navigateToStep('done');
                    }),
                )
                .subscribe();
        }
    }

    /**
     * Utility method to validate the purchaseLimit
     */
    private validateLimitsForPurchase(purchaseStart: PurchaseFlowDto, limitsForPurchase: BazaNcPurchaseFlowLimitsForPurchaseResponse) {
        const { amount, transactionFeesCents } = purchaseStart;
        const totalCents = amount + transactionFeesCents;

        const ccLimits = limitsForPurchase.find((limit) => limit.transactionType === BazaNcPurchaseFlowTransactionType.CreditCard);
        this.isPurchaseAboveLimit =
            !ccLimits?.isAvailable ||
            // Based on active Payment method CC fees are added and Total changes.
            // So, Manually verifying if total goes above limit? Yes, restrict the CC.
            totalCents + ccLimits?.feeCents > ccLimits?.maximumAmountCents;
    }

    public onBackClick(purchaseStart: PurchaseFlowDto): void {
        purchaseStart.fee = null;
        this.store.dispatch(new PatchStartPurchase(purchaseStart)).subscribe(() => {
            this.navigateToStep('agreement');
        });
    }

    get authorizationLinkConfig(): string {
        return `${this.i18nBasePath}.authorization.linkConfig`;
    }

    onUpdateSubmitBtnDisableStatus(isDisabled: boolean) {
        this.isSubmitBtnDisabled = isDisabled;
    }

    navigateToStep(url: string) {
        this.router.navigate([this.pfConfig?.pfLink, url], {
            queryParamsHandling: 'preserve',
        });
    }
}
